import type { Dispatch, ElementType, FunctionComponent, ReactElement } from 'react'
import { useContext } from 'react'
import React from 'react'
import { LinkButton, TypographyV2 as Typography } from '@which/seatbelt'
import { ChevronDownIcon, ChevronUpIcon } from '@which/seatbelt/src/components/Icons/Navigational'

import type { ShowMoreFilters as ShowMoreFiltersType } from '../../../../../shared/types'
import { ReviewsTemplateContext } from '../../ReviewsTemplateContext'
import styles from './ShowMoreFilters.module.scss'

const SHOW_MORE_INDEX = 6
const MINIMUM_FILTER_ITEMS = 10

export const ShowMoreFilters: FunctionComponent<ShowMoreFiltersProps> = ({
  index,
  itemsLength,
  externalId,
  children,
  showMoreCallback,
  code,
}) => {
  const {
    state: {
      filterContext: { showMoreFilters, setShowMoreFilters },
    },
  } = useContext(ReviewsTemplateContext)
  const showAllFilters = showMoreFilters?.[externalId]

  switch (true) {
    case itemsLength >= MINIMUM_FILTER_ITEMS && index === SHOW_MORE_INDEX && !showAllFilters:
      return (
        <ShowMoreFiltersButton
          externalId={externalId}
          showMoreValue={true}
          text="Show all"
          icon={ChevronDownIcon}
          showMoreCallback={showMoreCallback}
          showMoreFilters={showMoreFilters}
          setShowMoreFilters={setShowMoreFilters}
        />
      )
    case index < SHOW_MORE_INDEX && !showAllFilters:
    case itemsLength < MINIMUM_FILTER_ITEMS:
      return <>{children}</>
    // ensure brand filters are rendered in DOM
    case code === 'manufacturer' && index > SHOW_MORE_INDEX && index !== itemsLength - 1:
      return (
        <div className={showAllFilters ? styles.showFilter : styles.hideFilter}>{children}</div>
      )
    case showAllFilters:
      return (
        <>
          {children}
          {index === itemsLength - 1 && (
            <ShowMoreFiltersButton
              externalId={externalId}
              showMoreValue={false}
              text="Show less"
              icon={ChevronUpIcon}
              showMoreCallback={showMoreCallback}
              showMoreFilters={showMoreFilters}
              setShowMoreFilters={setShowMoreFilters}
            />
          )}
        </>
      )
    default:
      return null
  }
}

const ShowMoreFiltersButton: FunctionComponent<ShowMoreFiltersButtonProps> = ({
  externalId,
  showMoreValue,
  text,
  icon: Icon,
  showMoreCallback,
  showMoreFilters,
  setShowMoreFilters,
}) => {
  const onShowMoreClick = () => {
    if (setShowMoreFilters) {
      setShowMoreFilters({
        ...showMoreFilters,
        [externalId]: showMoreValue,
      })
      showMoreCallback && showMoreCallback(text)
    }
  }

  return (
    <div className={styles.showMoreFiltersButtonWrapper} data-testid="show-more-filters">
      <LinkButton onClick={onShowMoreClick} icon={Icon}>
        <Typography tag="span" textStyle="sb-text-interface-body-small-regular">
          {text}
        </Typography>
      </LinkButton>
    </div>
  )
}

///////IMPLEMENTATION/////////

type ShowMoreCallback = (eventAction: string) => void

type ShowMoreFiltersButtonProps = {
  externalId: string
  showMoreValue: boolean
  text: string
  icon: ElementType
  showMoreCallback: ShowMoreCallback
  showMoreFilters: ShowMoreFiltersType
  setShowMoreFilters: Dispatch<React.SetStateAction<ShowMoreFiltersType>>
}

type ShowMoreFiltersProps = {
  index: number
  itemsLength: number
  externalId: string
  children: ReactElement | ReactElement[]
  showMoreCallback: ShowMoreCallback
  code?: string
}
